/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useState } from "react";
import PropTypes from "prop-types";
import { TableRow } from "./TableRow";
import styles from "./Table.module.css";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import PopupDialog from "./PopupDialog";
import Moment from "moment";

function formatStatus(status) {
  if (typeof status !== "string") {
    console.error("Invalid status:", status); // Debugging log
    return "Unknown Status"; // Fallback value
  }
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function generateStatusBadgeClass(status) {
  if (typeof status !== "string") {
    console.error("Invalid status:", status); // Debugging log
    return "unknownStatusBadge"; // Fallback class
  }
  return (
    status
      .toLowerCase()
      .split("_")
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join("") + "StatusBadge"
  );
}

function DeleteMenuContent({ item }) {
  const deleteLabel = item.statuses.length > 1 ? (
    <Tooltip title="All the versions will be deleted, if you click on delete">
      <div className={styles.deleteAppCls}>
        <DeleteForeverOutlinedIcon className={styles.deleteAppIconCls} />
        &nbsp;Delete
      </div>
    </Tooltip>
  ) : (
    <div className={styles.deleteAppCls}>
      <DeleteForeverOutlinedIcon className={styles.deleteAppIconCls} />
      &nbsp;Delete
    </div>
  );
  return deleteLabel;
}

DeleteMenuContent.propTypes = {
  item: PropTypes.object.isRequired,
};

function ExpandToggle({ isOpen, onToggle }) {
  return isOpen ? (
    <ExpandLess className={styles.expandLessHover} onClick={onToggle} style={{ cursor: "pointer" }} />
  ) : (
    <ExpandMore className={styles.expandMoreHover} onClick={onToggle} style={{ cursor: "pointer" }} />
  );
}

ExpandToggle.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export const TableSection = ({ item, deleteItem, type, isOemMode = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleReadMore = () => {
    setIsOpen((prev) => !prev);
  };

  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const navigate = useNavigate();
  const handleRowClick = ($event, index) => {
    const submissionId =
      (typeof index === "number" && item.statuses[index]?.submissionId) ||
      $event?.submissionId ||
      item.statuses[0]?.submissionId;

    if (!submissionId) {
      return;
    }

    sessionStorage.setItem("submissionId", submissionId);

    if (isOemMode) {
      navigate(
        `/ui/developer/applications/${item.id}/submission/${submissionId}/oem-status`,
        {
          state: {
            teamId: item.teamId,
            packageName: item.packageName,
            appVersion: item.statuses[typeof index === "number" ? index : 0]?.version,
          },
        }
      );
      return;
    }

    navigate(`/ui/developer/applications/${item.id}`, {
      state: { teamId: item.teamId, submissionId: submissionId },
    });
  };

  const handleAppDetailsClick = ($event, index) => {
    const submissionId =
      (typeof index === "number" && item.statuses[index]?.submissionId) ||
      $event?.submissionId ||
      item.statuses[0]?.submissionId;

    if (!submissionId) {
      return;
    }

    sessionStorage.setItem("submissionId", submissionId);

    navigate(`/ui/developer/applications/${item.id}`, {
      state: { teamId: item.teamId, submissionId: submissionId },
    });
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDialog = () => {
    setOpenErrorPopup(false);
  };

  const handlePopupFromTableRow = () => {
    handlePopup();
  };

  const handlePopup = () => {
    setHeaderMessage(
      type === "application"
        ? `Delete App Listing - ${item.applicationName}`
        : `Delete Feature Listing - ${item.applicationName}`
    );
    setComponentName("delete");
    setOpenErrorPopup(true);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteAction = () => {
    setOpenErrorPopup(false);
    deleteItem({
      applicationId: item.id,
      applicationName: item.applicationName,
    });
  };

  return (
    <>
      {!item.isDeleted && (
        <tr className="tableRowNotDeletedAppList">
          <td
            className={styles.iconUrlColCls}
            style={{ cursor: "pointer" }}
            onClick={isOemMode ? handleAppDetailsClick : handleRowClick}
          >
            <img
              className={styles.appIconUrlImageCls}
              src={item.iconUrl}
              alt={`${type === "application" ? "app" : "feature"} icon`}
            />
          </td>
          <td
            className={styles.appNameCls}
            style={{ cursor: "pointer" }}
            onClick={isOemMode ? handleAppDetailsClick : handleRowClick}
          >
            <div
              title={item.applicationName}
              style={{
                width: "250px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.applicationName}
            </div>
            {type !== "feature" && (
              <div
                title={item.packageName}
                style={{
                  width: "200px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "#5A5B5C",
                  fontSize: "12px",
                  fontWeight: "400",
                }}
              >
                {item.packageName}
              </div>
            )}
          </td>

          {/* Category Column (Only for Features) */}
          {type === "feature" && (
            <td className={styles.categoryCls}>
              <span>{item.category}</span>
            </td>
          )}

          {/* Version Column */}
          <td
            className={styles.versionCls}
            style={{ cursor: "pointer" }}
            onClick={isOemMode ? handleAppDetailsClick : handleRowClick}
          >
            <div className={styles.versionDivCls}>
              {item.statuses[0].version || "-"}
              {item.statuses.length > 1 && (
                <svg
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.96732 8.0636L8.76079 7.37989V6.01249L9.96732 5.32878L11.1739 6.01249V7.37989L9.96732 8.0636ZM9.96732 9.91362L12.7826 8.3049V5.08748L9.96732 3.47876L7.15207 5.08748V8.3049L9.96732 9.91362ZM8.51948 14.3376H13.0239C12.93 14.6861 12.7692 14.9677 12.5413 15.1822C12.3134 15.3967 12.0184 15.5307 11.6565 15.5843L2.88898 16.6501C2.44659 16.7171 2.04776 16.6132 1.6925 16.3384C1.33724 16.0636 1.1328 15.705 1.07918 15.2626L0.0134059 6.47499C-0.0402179 6.0326 0.0670297 5.63712 0.335149 5.28857C0.603268 4.94001 0.958525 4.73892 1.40092 4.6853L2.32593 4.56464V6.17336L1.60201 6.2739L2.68789 15.0615L8.51948 14.3376ZM5.54336 12.7289C5.10096 12.7289 4.72225 12.5713 4.40721 12.2563C4.09217 11.9413 3.93465 11.5626 3.93465 11.1202V2.27223C3.93465 1.82983 4.09217 1.45111 4.40721 1.13607C4.72225 0.821033 5.10096 0.663513 5.54336 0.663513H14.3913C14.8337 0.663513 15.2124 0.821033 15.5274 1.13607C15.8425 1.45111 16 1.82983 16 2.27223V11.1202C16 11.5626 15.8425 11.9413 15.5274 12.2563C15.2124 12.5713 14.8337 12.7289 14.3913 12.7289H5.54336ZM5.54336 11.1202H14.3913V2.27223H5.54336V11.1202Z"
                    fill="#121415"
                    fillOpacity="0.6"
                  />
                </svg>
              )}
            </div>
          </td>

          {/* Status Column */}
          <td className={styles.statusCls}>
            {isOemMode ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <button
                  type="button"
                  style={{
                    color: "#0476E2",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 400,
                    background: "none",
                    border: "none",
                    padding: 0,
                    textDecoration: "underline",
                  }}
                  onClick={handleRowClick}
                >
                  View Current Status
                </button>
                {item.statuses.length > 1 && (
                  <ExpandToggle isOpen={isOpen} onToggle={toggleReadMore} />
                )}
              </div>
            ) : (
              <span
                className={
                  styles[generateStatusBadgeClass(item.statuses[0].status)]
                }
              >
                {formatStatus(item.statuses[0].status)}
              </span>
            )}
          </td>

          {/* Last Modified Column */}
          {!isOemMode && (
            <td className={styles.lastModifiedCls}>
              <div className={styles.dateColWithIconCls}>
                {Moment(item.lastModified).format("DD MMM YYYY")}
              </div>
            </td>
          )}

          {/* No of Installs Column (Only for Features) */}
          {type === "feature" && (
            <td className={styles.installsCls}>
              <span>{item.noOfInstalls}</span>
            </td>
          )}
          {type === "feature" && (
            <td className={styles.deleteFeatureMenuCls}>
                <div>
                  <MoreVertIcon
                    className={styles.moreVertIconHover}
                    onClick={handleClick}
                    id="action-button"
                    aria-controls={open ? "delete-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                  />

                  <Menu
                    id="delete-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    slotProps={{
                      list: {
                        "aria-labelledby": "action-button",
                      },
                    }}
                  >
                    <MenuItem onClick={handlePopup}>
                      <DeleteMenuContent item={item} />
                    </MenuItem>
                  </Menu>
                </div>
            </td>
          )}

          {type === "application" && !isOemMode && (
            <td className={styles.actionedCls}>
              <div className={styles.dateColWithIconCls}>
                <MoreVertIcon
                  className={styles.moreVertIconHover}
                  onClick={handleClick}
                  id="action-button"
                  aria-controls={open ? "delete-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                />

                <Menu
                  id="delete-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  slotProps={{
                    list: {
                      "aria-labelledby": "action-button",
                    },
                  }}
                >
                  <MenuItem onClick={handlePopup}>
                    <DeleteMenuContent item={item} />
                  </MenuItem>
                </Menu>

                {item.statuses.length > 1 && (
                  <ExpandToggle isOpen={isOpen} onToggle={toggleReadMore} />
                )}
              </div>
            </td>
          )}
        </tr>
      )}

      {item.isDeleted && (
        <tr
          className="tableRowIsDeletedAppList"
        >
          <td className={styles.iconUrlColCls}>
            <img
              className={styles.appIconUrlImageCls}
              src={item.iconUrl}
              alt={`${type === "application" ? "app" : "feature"} icon`}
            />
          </td>
          <td className={styles.appNameCls}>{item.applicationName}</td>

          <td className={styles.versionCls}>{item.statuses[0].version}</td>

          <td className={styles.statusCls}>
            <Tooltip
              title={`${
                type === "application" ? "Application" : "Feature"
              } waiting for admin approval for deletion, hence app details will not be shown`}
            >
              <span className={styles.delistedStatusBadge}>
                Waiting for Delete Approval
              </span>
            </Tooltip>
          </td>

          {!isOemMode && (
            <td className={styles.lastModifiedCls}>
              <div className={styles.dateColWithIconCls}>
                {Moment(item.lastModified).format("DD MMM YYYY")}
              </div>
            </td>
          )}

          {!isOemMode && (
            <td className={styles.actionedCls}>
            <div className={styles.dateColWithIconCls}>
              <Menu
                id="delete-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  list: {
                    "aria-labelledby": "action-button",
                  },
                }}
              >
                <MenuItem onClick={handlePopup}>
                  <div className={styles.deleteAppCls}>
                    <DeleteForeverOutlinedIcon
                      className={styles.deleteAppIconCls}
                    />
                    &nbsp;Delete
                  </div>
                </MenuItem>
              </Menu>
            </div>
          </td>
          )}
        </tr>
      )}

      {item.statuses.length > 1 &&
        isOpen &&
        !item.isDeleted &&
        item.statuses.map(
          (status, index) =>
            index > 0 && (
              <TableRow
                key={status.id ?? index}
                status={status}
                applicationDelete={item.isDeleted}
                handlePopupFromTableRow={handlePopupFromTableRow}
                handleRowClick={($event) => handleRowClick($event, index)}
                handleAppDetailsClick={($event) => handleAppDetailsClick($event, index)}
                isOemMode={isOemMode}
              />
            )
        )}
      <PopupDialog
        onClose={handleCloseDialog}
        openErrorPopup={openErrorPopup}
        headerMessage={headerMessage}
        componentName={componentName}
        handleDeleteApp={handleDeleteAction}
        type={type}
      />
    </>
  );
};

TableSection.propTypes = {
  item: PropTypes.object.isRequired,
  deleteItem: PropTypes.func,
  type: PropTypes.string.isRequired,
  isOemMode: PropTypes.bool,
};
